import { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Tag,
  Button,
  Space,
  Tabs,
  Empty,
  Modal,
  Form,
  Input,
  Select,
  message,
  Progress,
  Alert,
  Tooltip,
  Badge,
  Divider,
} from 'antd';
import {
  CloudServerOutlined,
  RocketOutlined,
  ApiOutlined,
  DollarOutlined,
  ThunderboltOutlined,
  PlusOutlined,
  DeleteOutlined,
  LineChartOutlined,
  WarningOutlined,
  EditOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { Line, Column } from '@ant-design/plots';
import { gpuInstancesApi, GpuInstance, GpuProvider } from '../../api/gpuInstances';
import TerminalModal from '../../components/Training/TerminalModal';

const { TabPane } = Tabs;
const { Option } = Select;

export default function GpuInstancesPage() {
  const [providers, setProviders] = useState<GpuProvider[]>([]);
  const [instances, setInstances] = useState<GpuInstance[]>([]);
  const [loading, setLoading] = useState(false);
  const [addProviderModal, setAddProviderModal] = useState(false);
  const [form] = Form.useForm();
  
  // Provider selection state - provider-first approach
  const [selectedProvider, setSelectedProvider] = useState<GpuProvider | null>(null);
  
  // Terminal state
  const [terminalVisible, setTerminalVisible] = useState(false);
  const [selectedInstance, setSelectedInstance] = useState<GpuInstance | null>(null);

  // Edit provider state
  const [editProviderModal, setEditProviderModal] = useState(false);
  const [editingProvider, setEditingProvider] = useState<GpuProvider | null>(null);
  const [editForm] = Form.useForm();

  // Help modal state
  const [helpModalVisible, setHelpModalVisible] = useState(false);

  // Debug modal state
  useEffect(() => {
    console.log('[GPU] addProviderModal state changed to:', addProviderModal);
  }, [addProviderModal]);

  useEffect(() => {
    loadData();
  }, []);

  // Auto-select first provider when providers list changes
  useEffect(() => {
    if (providers.length > 0 && !selectedProvider) {
      setSelectedProvider(providers[0]);
    }
  }, [providers, selectedProvider]);

  // Load instances when selected provider changes
  useEffect(() => {
    if (selectedProvider) {
      loadInstances();
    }
  }, [selectedProvider]);

  const loadData = async () => {
    setLoading(true);
    try {
      // First, fetch providers
      const providersRes = await gpuInstancesApi.getProviders();
      const providersData = Array.isArray(providersRes.data) ? providersRes.data : [];
      setProviders(providersData);
    } catch (error: any) {
      console.error('Failed to load GPU providers:', error);
      
      if (error?.response?.status !== 404) {
        message.error('Failed to load GPU providers');
      }
      
      setProviders([]);
    } finally {
      setLoading(false);
    }
  };

  const loadInstances = async () => {
    if (!selectedProvider) return;
    
    setLoading(true);
    try {
      const instancesRes = await gpuInstancesApi.listInstances();
      setInstances(Array.isArray(instancesRes.data) ? instancesRes.data : []);
    } catch (error: any) {
      console.error('Failed to load GPU instances data:', error);
      
      // Only show error message if it's not a 404 (no instances)
      if (error?.response?.status !== 404) {
        message.error('Failed to load GPU instances data');
      }
      
      setInstances([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddProvider = async (values: any) => {
    try {
      await gpuInstancesApi.createProvider(values);
      message.success('Provider added successfully!');
      setAddProviderModal(false);
      form.resetFields();
      loadData();
    } catch (error) {
      message.error('Failed to add provider');
    }
  };

  const handleDestroyInstance = async (instanceId: string) => {
    Modal.confirm({
      title: 'Destroy Instance?',
      content: 'This will permanently delete the instance and stop billing. This action cannot be undone.',
      okText: 'Destroy',
      okType: 'danger',
      onOk: async () => {
        try {
          await gpuInstancesApi.destroyInstance(instanceId);
          message.success('Instance destroyed');
          loadData();
        } catch (error) {
          message.error('Failed to destroy instance');
        }
      },
    });
  };

  const handleDeleteProvider = async (providerId: string) => {
    const provider = providers.find(p => p.id === providerId);
    
    Modal.confirm({
      title: 'Delete Provider?',
      content: `This will permanently delete "${provider?.name}" and all its instances. This action cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await gpuInstancesApi.deleteProvider(providerId);
          message.success('Provider deleted');
          
          // If deleted provider was selected, clear selection
          if (selectedProvider?.id === providerId) {
            setSelectedProvider(null);
          }
          
          await loadData();
        } catch (error) {
          message.error('Failed to delete provider');
        }
      },
    });
  };

  const handleUpdateProvider = async (values: any) => {
    if (!editingProvider) return;
    
    try {
      await gpuInstancesApi.updateProvider(editingProvider.id, {
        apiKey: values.apiKey || undefined,
        sshKey: values.sshKey || undefined,
      });
      message.success('Provider updated successfully');
      setEditProviderModal(false);
      editForm.resetFields();
      loadData();
    } catch (error) {
      message.error('Failed to update provider');
    }
  };

  const handleSyncInstances = async () => {
    if (!selectedProvider) {
      message.warning('Please select a provider first');
      return;
    }
    
    try {
      setLoading(true);
      await gpuInstancesApi.syncInstances(selectedProvider.id);
      message.success(`Instances synced from ${selectedProvider.name}!`);
      await loadInstances();
    } catch (error) {
      message.error('Failed to sync instances. Make sure the provider API key is valid.');
    } finally {
      setLoading(false);
    }
  };

  // Calculate dashboard metrics
  const trainingInstances = instances.filter(i => i.agentType === 'training');
  const hostInstances = instances.filter(i => i.agentType === 'host');
  const runningInstances = instances.filter(i => i.status === 'running');
  const totalCostPerHour = runningInstances.reduce((sum, i) => sum + i.pricePerHour, 0);
  const totalCost = instances.reduce((sum, i) => sum + i.totalCost, 0);
  const totalRequests = hostInstances.reduce((sum, i) => sum + (i.requestCount || 0), 0);
  const avgLatency = hostInstances.length > 0
    ? hostInstances.reduce((sum, i) => sum + (i.avgLatencyMs || 0), 0) / hostInstances.length
    : 0;

  // Cost over time data - calculated from instances
  const costData = useMemo(() => {
    const days = 7;
    const result: { date: string; cost: number }[] = [];
    const today = new Date();
    
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      date.setHours(0, 0, 0, 0);
      
      const nextDate = new Date(date);
      nextDate.setDate(nextDate.getDate() + 1);
      
      // Calculate cost for this day based on active instances
      let dailyCost = 0;
      instances.forEach(instance => {
        const createdAt = new Date(instance.createdAt);
        const destroyedAt = (instance as any).destroyedAt ? new Date((instance as any).destroyedAt) : null;
        
        // Instance was active on this day if:
        // - Created before end of this day AND
        // - Not destroyed OR destroyed after start of this day
        if (createdAt < nextDate && (!destroyedAt || destroyedAt > date)) {
          // Calculate hours active on this day
          const startTime = Math.max(createdAt.getTime(), date.getTime());
          const endTime = destroyedAt 
            ? Math.min(destroyedAt.getTime(), nextDate.getTime())
            : (i === 0 ? Date.now() : nextDate.getTime());
          const hoursActive = Math.max(0, (endTime - startTime) / (1000 * 60 * 60));
          dailyCost += hoursActive * instance.pricePerHour;
        }
      });
      
      const dateLabel = i === 0 ? 'Today' : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      result.push({ date: dateLabel, cost: parseFloat(dailyCost.toFixed(2)) });
    }
    
    return result;
  }, [instances]);

  // Instance usage data
  const usageData = instances.map(i => ({
    name: i.name,
    cost: i.totalCost,
    hours: i.totalCost / i.pricePerHour,
  }));

  const instanceColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: GpuInstance) => (
        <Space direction="vertical" size={0}>
          <strong>{name}</strong>
          <small style={{ color: '#888' }}>
            {record.gpuType} x{record.numGpus}
          </small>
        </Space>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'agentType',
      key: 'agentType',
      render: (type: string) => (
        <Tag color={type === 'training' ? 'blue' : 'green'}>
          {type === 'training' ? <RocketOutlined /> : <ApiOutlined />} {type.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const colors: Record<string, string> = {
          running: 'success',
          creating: 'processing',
          stopped: 'default',
          error: 'error',
        };
        return <Badge status={colors[status] as any} text={status.toUpperCase()} />;
      },
    },
    {
      title: 'Progress',
      key: 'progress',
      render: (_: any, record: GpuInstance) => {
        if (record.agentType === 'training' && record.currentEpoch && record.totalEpochs) {
          const percent = (record.currentEpoch / record.totalEpochs) * 100;
          return (
            <div style={{ width: 150 }}>
              <Progress
                percent={Math.round(percent)}
                size="small"
                status="active"
              />
              <small>Epoch {record.currentEpoch}/{record.totalEpochs}</small>
            </div>
          );
        }
        if (record.agentType === 'host') {
          return (
            <Space direction="vertical" size={0}>
              <small>{record.requestCount || 0} requests</small>
              <small>{record.avgLatencyMs?.toFixed(1) || 0}ms avg</small>
            </Space>
          );
        }
        return '-';
      },
    },
    {
      title: 'Cost',
      key: 'cost',
      render: (_: any, record: GpuInstance) => (
        <Space direction="vertical" size={0}>
          <strong>${record.totalCost.toFixed(2)}</strong>
          <small style={{ color: '#888' }}>${record.pricePerHour}/hr</small>
        </Space>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: GpuInstance) => (
        <Space>
          {record.status === 'running' && (
            <Tooltip title="Connect (Opens in New Tab)">
              <Button
                size="small"
                type="primary"
                icon={<ThunderboltOutlined />}
                onClick={() => {
                  window.open(
                    `/training/gpu-instances/${record.id}/connect?name=${encodeURIComponent(record.name)}`,
                    '_blank'
                  );
                }}
              >
                Connect
              </Button>
            </Tooltip>
          )}
          {record.status === 'stopped' && (
            <Tooltip title="Start Instance">
              <Button
                size="small"
                type="primary"
                loading={loading}
                onClick={async () => {
                  try {
                    await gpuInstancesApi.startInstance(record.id);
                    message.success('Instance starting...');
                    loadData();
                  } catch (error) {
                    message.error('Failed to start instance');
                  }
                }}
              >
                Start
              </Button>
            </Tooltip>
          )}
          {record.status === 'running' && (
            <Tooltip title="Stop Instance">
              <Button
                size="small"
                loading={loading}
                onClick={async () => {
                  try {
                    await gpuInstancesApi.stopInstance(record.id);
                    message.success('Instance stopping...');
                    loadData();
                  } catch (error) {
                    message.error('Failed to stop instance');
                  }
                }}
              >
                Stop
              </Button>
            </Tooltip>
          )}
          <Tooltip title="Destroy Instance">
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDestroyInstance(record.id)}
            >
              Destroy
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <Row justify="space-between" align="middle">
          <Col>
            <h1 style={{ margin: 0 }}>
              <CloudServerOutlined /> GPU Instances
            </h1>
            <p style={{ color: '#888', marginTop: '8px' }}>
              Manage cloud GPU instances for training and hosting
            </p>
          </Col>
          {providers.length > 0 && (
            <Col>
              <Space>
                <Select
                  placeholder="Select Provider"
                  value={selectedProvider?.id}
                  onChange={(providerId) => {
                    const provider = providers.find(p => p.id === providerId);
                    if (provider) setSelectedProvider(provider);
                  }}
                  style={{ width: 200 }}
                >
                  {providers.map(p => (
                    <Option key={p.id} value={p.id}>
                      {p.name} {!p.isActive && '(Inactive)'}
                    </Option>
                  ))}
                </Select>
                <Button
                  icon={<PlusOutlined />}
                  onClick={() => setAddProviderModal(true)}
                >
                  Add Provider
                </Button>
                <Button
                  icon={<ThunderboltOutlined />}
                  onClick={handleSyncInstances}
                  loading={loading}
                  disabled={!selectedProvider}
                >
                  Sync Instances
                </Button>
              </Space>
            </Col>
          )}
        </Row>
      </div>

      {/* Onboarding Screen - Show when no providers */}
      {providers.length === 0 && (
        <Card>
          <Empty
            image={<CloudServerOutlined style={{ fontSize: 80, color: '#1890ff' }} />}
            description={
              <div style={{ marginTop: 16 }}>
                <h2>Get Started with GPU Instances</h2>
                <p style={{ color: '#888', marginBottom: 24 }}>
                  Connect your cloud GPU provider to start creating instances for training and hosting LLM models.
                </p>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <Card style={{ textAlign: 'left', maxWidth: 600, margin: '0 auto' }}>
                    <h3>✨ What you can do:</h3>
                    <ul style={{ color: '#666', lineHeight: '1.8' }}>
                      <li><strong>Training Agents:</strong> High-performance GPUs for fine-tuning models</li>
                      <li><strong>Host Agents:</strong> Deploy models for API inference</li>
                      <li><strong>Real-time Terminal:</strong> SSH access directly in your browser</li>
                      <li><strong>Cost Tracking:</strong> Monitor spending and optimize usage</li>
                      <li><strong>Auto-deployment:</strong> One-click agent setup</li>
                    </ul>
                  </Card>
                  <Button
                    type="primary"
                    size="large"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      console.log('[GPU] Add Provider button clicked');
                      setAddProviderModal(true);
                    }}
                    style={{ marginTop: 16 }}
                  >
                    Add Your First Provider
                  </Button>
                  <p style={{ color: '#999', fontSize: 12 }}>
                    Currently supporting Vast.ai • RunPod and Lambda Labs coming soon
                  </p>
                </Space>
              </div>
            }
          />
        </Card>
      )}

      {/* Main Dashboard - Show when providers exist */}
      {providers.length > 0 && (
        <>
          {/* Phase 9: Dashboard Metrics */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Active Instances"
              value={runningInstances.length}
              prefix={<CloudServerOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#888' }}>
              {trainingInstances.length} training, {hostInstances.length} host
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Current Cost/Hour"
              value={totalCostPerHour}
              prefix={<DollarOutlined />}
              precision={2}
              valueStyle={{ color: '#cf1322' }}
            />
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#888' }}>
              ${(totalCostPerHour * 720).toFixed(2)}/month estimated
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Spend"
              value={totalCost}
              prefix={<DollarOutlined />}
              precision={2}
            />
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#888' }}>
              Lifetime across all instances
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="API Requests"
              value={totalRequests}
              prefix={<ApiOutlined />}
              suffix={avgLatency > 0 ? `@ ${avgLatency.toFixed(0)}ms` : ''}
            />
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#888' }}>
              {hostInstances.length} host agents serving
            </div>
          </Card>
        </Col>
      </Row>

      {/* Phase 9: Cost Tracking Chart */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} lg={16}>
          <Card title={<><LineChartOutlined /> Cost Over Time</>}>
            <Line
              data={costData}
              xField="date"
              yField="cost"
              smooth
              height={250}
              point={{ size: 5, shape: 'diamond' }}
              label={{
                style: {
                  fill: '#aaa',
                  fontSize: 12,
                },
              }}
              yAxis={{
                label: {
                  formatter: (v: string) => `$${v}`,
                },
              }}
            />
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card title={<><DollarOutlined /> Cost by Instance</>}>
            <Column
              data={usageData.slice(0, 5)}
              xField="name"
              yField="cost"
              height={250}
              label={{
                position: 'top',
                formatter: (datum: any) => `$${datum.cost.toFixed(2)}`,
              }}
              yAxis={{
                label: {
                  formatter: (v: string) => `$${v}`,
                },
              }}
            />
          </Card>
        </Col>
      </Row>

      {/* Phase 9: Usage Alerts */}
      {totalCostPerHour > 1.0 && (
        <Alert
          message="High Usage Alert"
          description={`You're currently spending $${totalCostPerHour.toFixed(2)}/hour ($${(totalCostPerHour * 720).toFixed(2)}/month). Consider stopping unused instances.`}
          type="warning"
          icon={<WarningOutlined />}
          showIcon
          closable
          style={{ marginBottom: '24px' }}
        />
      )}

      {/* Provider Cards */}
      {providers.length > 0 && (
        <div style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <h3 style={{ margin: 0 }}>Providers</h3>
              <Tooltip title="Connection Setup Guide">
                <Button 
                  type="text" 
                  shape="circle"
                  size="small"
                  icon={<QuestionCircleOutlined style={{ color: '#888' }} />} 
                  onClick={() => setHelpModalVisible(true)}
                />
              </Tooltip>
            </div>
          </div>
          <Row gutter={[16, 16]}>
            {providers.map(provider => (
              <Col key={provider.id} xs={24} sm={12} md={8}>
                <Card>
                  <Statistic
                    title={provider.name}
                    value={provider._count?.instances || 0}
                    suffix="instances"
                    prefix={<ThunderboltOutlined />}
                  />
                  <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Tag color={provider.isActive ? 'green' : 'red'}>
                      {provider.isActive ? 'Active' : 'Inactive'}
                    </Tag>
                    <Space>
                      <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => {
                          setEditingProvider(provider);
                          editForm.setFieldsValue({ apiKey: '', sshKey: '' });
                          setEditProviderModal(true);
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => handleDeleteProvider(provider.id)}
                      >
                        Delete
                      </Button>
                    </Space>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      )}

      {/* Instances Table */}
      <Card title="All Instances">
        <Tabs defaultActiveKey="all">
          <TabPane tab={`All (${instances.length})`} key="all">
            <Table
              columns={instanceColumns}
              dataSource={instances}
              rowKey="id"
              loading={loading}
              locale={{
                emptyText: (
                  <Empty
                    description="No instances yet. Create your first GPU instance to get started!"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                ),
              }}
            />
          </TabPane>
          <TabPane tab={`Training (${trainingInstances.length})`} key="training">
            <Table
              columns={instanceColumns}
              dataSource={trainingInstances}
              rowKey="id"
              loading={loading}
            />
          </TabPane>
          <TabPane tab={`Host (${hostInstances.length})`} key="host">
            <Table
              columns={instanceColumns}
              dataSource={hostInstances}
              rowKey="id"
              loading={loading}
            />
          </TabPane>
        </Tabs>
      </Card>

        </>
      )}

      {/* Add Provider Modal - Placed outside conditional to always render */}
      <Modal
        title="Add GPU Provider"
        open={addProviderModal}
        onCancel={() => setAddProviderModal(false)}
        onOk={() => form.submit()}
        width={600}
      >
        <Form form={form} onFinish={handleAddProvider} layout="vertical">
          <Form.Item
            name="type"
            label="Provider"
            rules={[{ required: true, message: 'Please select a provider' }]}
          >
            <Select placeholder="Select provider">
              <Option value="vastai">Vast.ai</Option>
              <Option value="runpod" disabled>RunPod (Coming Soon)</Option>
              <Option value="lambda" disabled>Lambda Labs (Coming Soon)</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="apiKey"
            label={<span>API Key <a href="https://cloud.vast.ai/account/" target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, marginLeft: 8 }}>Get API Key →</a></span>}
            rules={[{ required: true, message: 'Please enter API key' }]}
          >
            <Input.Password placeholder="Enter your API key" />
          </Form.Item>
          <Form.Item
            name="sshKey"
            label={<span>SSH Private Key <a href="https://cloud.vast.ai/account/" target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, marginLeft: 8 }}>Manage SSH Keys →</a></span>}
            extra="Paste your private key (~/.ssh/id_rsa). Public key must be added to Vast.ai → Account → SSH Public Keys."
          >
            <Input.TextArea 
              placeholder={`-----BEGIN OPENSSH PRIVATE KEY-----\n...\n-----END OPENSSH PRIVATE KEY-----`}
              rows={6}
              style={{ fontFamily: 'monospace', fontSize: 12 }}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Provider Modal */}
      <Modal
        title={`Edit ${editingProvider?.name || 'Provider'}`}
        open={editProviderModal}
        onCancel={() => {
          setEditProviderModal(false);
          editForm.resetFields();
        }}
        onOk={() => editForm.submit()}
        width={600}
      >
        <Alert
          message="Leave fields empty to keep current values"
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Form form={editForm} onFinish={handleUpdateProvider} layout="vertical">
          <Form.Item
            name="apiKey"
            label={<span>New API Key <a href="https://cloud.vast.ai/account/" target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, marginLeft: 8 }}>Get API Key →</a></span>}
          >
            <Input.Password placeholder="Enter new API key (leave empty to keep current)" />
          </Form.Item>
          <Form.Item
            name="sshKey"
            label={<span>New SSH Private Key <a href="https://cloud.vast.ai/account/" target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, marginLeft: 8 }}>Manage SSH Keys →</a></span>}
            extra="Paste your private key (~/.ssh/id_rsa). Public key must be added to Vast.ai."
          >
            <Input.TextArea 
              placeholder={`-----BEGIN OPENSSH PRIVATE KEY-----\n...\n-----END OPENSSH PRIVATE KEY-----`}
              rows={6}
              style={{ fontFamily: 'monospace', fontSize: 12 }}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Terminal Modal */}
      {selectedInstance && (
        <TerminalModal
          visible={terminalVisible}
          instanceId={selectedInstance.id}
          instanceName={selectedInstance.name}
          onClose={() => {
            setTerminalVisible(false);
            setSelectedInstance(null);
          }}
        />
      )}
      <Modal
        title="How to Connect to GPU Instances"
        open={helpModalVisible}
        onCancel={() => setHelpModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setHelpModalVisible(false)}>
            Close
          </Button>
        ]}
        width={650}
      >
        <div style={{ marginTop: 8 }}>
          <Alert
            message="Secure Connection Required"
            description="To use the in-app terminal, you need to configure SSH keys correctly on both Vast.ai and this app."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Tabs defaultActiveKey="mac">
            <TabPane tab="Mac / Linux" key="mac">
              <h4>Step 1: Check your SSH Keys</h4>
              <p>Open <b>Terminal</b> and check for existing keys:</p>
              <pre style={{ background: '#f5f5f5', padding: 8, borderRadius: 4 }}>
ls -la ~/.ssh/id_rsa.pub
              </pre>
              <p>If not found, generate one (press Enter for default file):</p>
              <pre style={{ background: '#f5f5f5', padding: 8, borderRadius: 4 }}>
ssh-keygen -t rsa -b 4096
              </pre>

              <h4>Step 2: Add Public Key to Vast.ai</h4>
              <p>Copy your <b>PUBLIC</b> key:</p>
              <pre style={{ background: '#f5f5f5', padding: 8, borderRadius: 4 }}>
cat ~/.ssh/id_rsa.pub | pbcopy
# Or just: cat ~/.ssh/id_rsa.pub
              </pre>
              <p>Go to <b>Vast.ai Console &gt; Account &gt; SSH Keys</b> and add this key.</p>

              <h4>Step 3: Add Private Key to App</h4>
              <p>Copy your <b>PRIVATE</b> key:</p>
              <pre style={{ background: '#f5f5f5', padding: 8, borderRadius: 4 }}>
cat ~/.ssh/id_rsa | pbcopy
# Or just: cat ~/.ssh/id_rsa
              </pre>
              <p>
                1. In this app, find the <b>Vast.ai</b> card under "Providers".<br/>
                2. Click <EditOutlined /> <b>Edit</b>.<br/>
                3. Paste the full content into the <b>SSH Private Key</b> field.
              </p>
            </TabPane>

            <TabPane tab="Windows (PowerShell)" key="windows">
              <h4>Step 1: Check your SSH Keys</h4>
              <p>Open <b>PowerShell</b> and check for keys:</p>
              <pre style={{ background: '#f5f5f5', padding: 8, borderRadius: 4 }}>
Get-ChildItem ~\.ssh\id_rsa.pub
              </pre>
              <p>If not found, generate one:</p>
              <pre style={{ background: '#f5f5f5', padding: 8, borderRadius: 4 }}>
ssh-keygen -t rsa -b 4096
              </pre>

              <h4>Step 2: Add Public Key to Vast.ai</h4>
              <p>Copy your <b>PUBLIC</b> key:</p>
              <pre style={{ background: '#f5f5f5', padding: 8, borderRadius: 4 }}>
Get-Content ~\.ssh\id_rsa.pub | Set-Clipboard
              </pre>
              <p>Go to <b>Vast.ai Console &gt; Account &gt; SSH Keys</b> and add this key.</p>

              <h4>Step 3: Add Private Key to App</h4>
              <p>Copy your <b>PRIVATE</b> key:</p>
              <pre style={{ background: '#f5f5f5', padding: 8, borderRadius: 4 }}>
Get-Content ~\.ssh\id_rsa | Set-Clipboard
              </pre>
              <p>
                1. In this app, find the <b>Vast.ai</b> card under "Providers".<br/>
                2. Click <EditOutlined /> <b>Edit</b>.<br/>
                3. Paste the full content into the <b>SSH Private Key</b> field.
              </p>
            </TabPane>
          </Tabs>

          <Divider style={{ margin: '16px 0' }} />

          <h4>Final Step: Launch & Connect</h4>
          <p>
            1. Create a <b>NEW instance</b> (Keys are only added at creation time).<br/>
            2. Wait for it to start.<br/>
            3. Click <ThunderboltOutlined /> <b>Connect</b>.
          </p>
        </div>
      </Modal>
    </div>
  );
}
